import React, { useState, useEffect, useCallback } from 'react';
import AgentService from '../../services/agentService';

interface Conversation {
  agent_id: string;
  conversation_id: string;
  start_time_unix_secs: number;
  call_duration_secs?: number;
  message_count?: number;
  status: string;
  call_successful?: string;
  agent_name?: string;
  from_number?: string;
  caller_id?: string;
}

interface ConversationHistoryProps {
  apiKey: string;
}

// Audio Player Component
const AudioPlayer: React.FC<{ 
  conversationId: string; 
  apiKey: string; 
  audioType: 'user' | 'response' | 'full';
  title: string;
}> = ({ conversationId, apiKey, audioType, title }) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const fetchAudioUrl = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = audioType === 'full' 
        ? `https://api.elevenlabs.io/v1/convai/conversations/${conversationId}/audio`
        : `https://api.elevenlabs.io/v1/convai/conversations/${conversationId}/audio/${audioType}`;
      
      const response = await fetch(endpoint, {
        headers: {
          'xi-api-key': apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch audio: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch audio');
    } finally {
      setLoading(false);
    }
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  return (
    <div className="glass-effect rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
        <h5 className="font-medium text-white flex items-center space-x-2 text-sm sm:text-base">
          <span className="text-base sm:text-lg">🎵</span>
          <span>{title}</span>
        </h5>
        {!audioUrl ? (
          <button
            onClick={fetchAudioUrl}
            disabled={loading}
            className="btn-primary px-3 sm:px-4 py-2 text-xs sm:text-sm w-full sm:w-auto"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Loading...</span>
              </div>
            ) : (
              '🎧 Load Audio'
            )}
          </button>
        ) : (
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              onClick={togglePlayPause}
              className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-full flex items-center justify-center text-white hover:scale-105 transition-transform flex-shrink-0"
            >
              <span className="text-sm sm:text-base">{isPlaying ? '⏸️' : '▶️'}</span>
            </button>
            <div className="text-xs text-gray-400">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="text-red-300 text-xs sm:text-sm flex items-center space-x-2">
          <span>❌</span>
          <span>{error}</span>
        </div>
      )}

      {audioUrl && (
        <>
          <audio
            ref={audioRef}
            src={audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
          <div className="space-y-2">
            <input
              type="range"
              min={0}
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / duration) * 100}%, #4b5563 ${(currentTime / duration) * 100}%, #4b5563 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ConversationHistory: React.FC<ConversationHistoryProps> = ({ apiKey }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [conversationDetails, setConversationDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);

  const fetchConversations = useCallback(async () => {
    if (!apiKey) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const agentService = new AgentService(apiKey);
      const userConversations = await agentService.getUserConversations();
      setConversations(userConversations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  const fetchConversationDetails = async (conversationId: string) => {
    setDetailsLoading(true);
    
    try {
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/conversations/${conversationId}`, {
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch conversation details: ${response.statusText}`);
      }

      const data = await response.json();

      // Extract caller ID from response (checking multiple possible locations)
      const callerId =
        data.metadata?.phone_call?.external_number ||
        data.conversation_initiation_client_data?.dynamic_variables?.system__caller_id ||
        null;

      // Attach callerId to details for easy access
      data.caller_id = callerId;

      // Update conversation list item with caller_id if it wasn't set yet
      if (callerId) {
        setConversations((prev) =>
          prev.map((c) =>
            c.conversation_id === conversationId ? { ...c, caller_id: callerId } : c
          )
        );
      }

      setConversationDetails(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch conversation details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const deleteConversation = async (conversationId: string) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;

    try {
      const response = await fetch(`https://api.elevenlabs.io/v1/convai/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'xi-api-key': apiKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete conversation: ${response.statusText}`);
      }

      await fetchConversations();
      if (selectedConversation === conversationId) {
        setSelectedConversation(null);
        setConversationDetails(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete conversation');
    }
  };

  const handleConversationClick = (conversationId: string) => {
    setSelectedConversation(conversationId);
    fetchConversationDetails(conversationId);
  };

  const formatDate = (unixTimestamp: number) => {
    return new Date(unixTimestamp * 1000).toLocaleString();
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 border-green-500/30 text-green-300';
      case 'in-progress':
        return 'bg-blue-500/20 border-blue-500/30 text-blue-300';
      case 'failed':
        return 'bg-red-500/20 border-red-500/30 text-red-300';
      default:
        return 'bg-gray-500/20 border-gray-500/30 text-gray-300';
    }
  };

  const loadMore = () => {
    setVisibleCount((prev) => prev + 10);
  };

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  if (!apiKey) {
    return (
      <div className="p-8 text-center">
        <div className="mb-6">
          <div className="text-6xl mb-4">🔑</div>
          <h3 className="text-xl font-semibold text-gray-300 mb-2">API Key Required</h3>
          <p className="text-gray-400">Please configure your API key in Settings to view conversations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Conversation History</h2>
          <p className="text-gray-400 text-sm sm:text-base">View and manage conversation logs from your agents</p>
        </div>
        <button
          onClick={fetchConversations}
          className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
        >
          <span>🔄</span>
          <span>Refresh</span>
        </button>
      </div>

      {error && (
        <div className="glass-card rounded-2xl p-4 mb-6 border border-red-500/30 neon-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-red-400 text-xl">❌</span>
              <p className="text-red-300 text-sm sm:text-base">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">×</button>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 sm:h-12 w-8 sm:w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-300 text-base sm:text-lg">Loading conversations...</p>
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
          {/* Conversations List */}
          <div className="glass-card rounded-2xl p-4 sm:p-6 flex flex-col max-h-[80vh]">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6 flex items-center flex-shrink-0">
              <span className="text-xl sm:text-2xl mr-2 sm:mr-3">💬</span>
              Recent Conversations
            </h3>
            {conversations.length === 0 ? (
              <div className="text-center py-8 sm:py-12 text-gray-400">
                <div className="text-4xl sm:text-6xl mb-4">💬</div>
                <h4 className="text-base sm:text-lg font-medium text-gray-300 mb-2">No conversations found</h4>
                <p className="text-sm sm:text-base">Start a conversation with your agents to see history here.</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto space-y-3 sm:space-y-4 pr-1">
                  {conversations.slice(0, visibleCount).map((conversation) => (
                  <div
                    key={conversation.conversation_id}
                      className={`glass-effect rounded-xl p-3 sm:p-4 cursor-pointer transition-all duration-300 hover:scale-[1.02] ${
                      selectedConversation === conversation.conversation_id
                        ? 'border border-blue-500/50 bg-blue-500/10'
                        : 'border border-white/10 hover:border-white/20'
                    }`}
                    onClick={() => handleConversationClick(conversation.conversation_id)}
                  >
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 space-y-2 sm:space-y-0">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-1 sm:mb-2">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-sm sm:text-lg">🤖</span>
                          </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-white text-sm sm:text-base truncate">
                              {conversation.agent_name || 'Unknown Agent'}
                            </h4>
                              <p className="text-xs sm:text-sm text-gray-400 truncate">
                                {conversation.caller_id || 'Caller: Unknown'}
                            </p>
                            </div>
                          </div>
                          <p className="text-[10px] sm:text-xs text-gray-500">
                            {formatDate(conversation.start_time_unix_secs)}
                          </p>
                        </div>
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(conversation.status)} flex-shrink-0`}>
                        {conversation.status}
                      </span>
                    </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between text-[10px] sm:text-xs text-gray-400 mb-2 sm:mb-3 space-y-1 sm:space-y-0">
                      <span className="flex items-center space-x-1">
                          <span>📞</span>
                          <span>{conversation.call_duration_secs ? formatDuration(conversation.call_duration_secs) : '--'}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <span>💬</span>
                        <span>{conversation.message_count || 0} messages</span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
                {visibleCount < conversations.length && (
                  <button
                    onClick={loadMore}
                    className="mt-4 self-center btn-secondary px-4 py-2 text-xs sm:text-sm"
                  >
                    Load More
                  </button>
                )}
              </>
            )}
          </div>

          {/* Conversation Details */}
          <div className="glass-card rounded-2xl p-4 sm:p-6 flex flex-col max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center flex-shrink-0">
                <span className="text-xl sm:text-2xl mr-2 sm:mr-3">📄</span>
                Conversation Details
              </h3>
              {selectedConversation && (
                <button
                  onClick={() => deleteConversation(selectedConversation)}
                  className="glass-effect hover:bg-red-500/20 border border-red-500/30 text-red-300 hover:text-red-200 px-3 py-2 rounded-xl transition-all duration-300 text-xs sm:text-sm"
                >
                  🗑️ Delete
                </button>
              )}
            </div>
            {!selectedConversation ? (
              <div className="text-center py-12 sm:py-16 text-gray-400">
                <div className="text-4xl sm:text-6xl mb-4">📄</div>
                <h4 className="text-base sm:text-lg font-medium text-gray-300 mb-2">Select a conversation</h4>
                <p className="text-sm sm:text-base">Click on a conversation to view its details and transcript.</p>
              </div>
            ) : detailsLoading ? (
              <div className="text-center py-12 sm:py-16">
                <div className="inline-block animate-spin rounded-full h-8 sm:h-12 w-8 sm:w-12 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-gray-300 text-sm sm:text-base">Loading details...</p>
              </div>
            ) : conversationDetails ? (
              <div className="space-y-4 sm:space-y-6">
                <div className="glass-effect rounded-xl p-3 sm:p-4">
                  <h4 className="font-semibold text-white mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
                    <span className="text-base sm:text-lg mr-2">ℹ️</span>
                    Conversation Info
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                    <div className="space-y-1 sm:space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center">
                        <span className="text-gray-400">Status:</span>
                        <span className={`ml-0 sm:ml-2 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(conversationDetails.status)} w-fit`}>
                          {conversationDetails.status}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center">
                        <span className="text-gray-400">Caller:</span>
                        <span className="ml-0 sm:ml-2 text-gray-300 truncate max-w-[140px]">
                          {conversationDetails.caller_id || conversationDetails.metadata?.phone_call?.external_number || 'Unknown'}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1 sm:space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center">
                        <span className="text-gray-400">Duration:</span>
                        <span className="ml-0 sm:ml-2 text-gray-300">{formatDuration(conversationDetails.metadata?.call_duration_secs || 0)}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center">
                        <span className="text-gray-400">Started:</span>
                        <span className="ml-0 sm:ml-2 text-gray-300 truncate max-w-[140px]">{formatDate(conversationDetails.start_time_unix_secs)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {conversationDetails.transcript && conversationDetails.transcript.length > 0 && (
                  <div className="glass-effect rounded-xl p-3 sm:p-4">
                    <h4 className="font-semibold text-white mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
                      <span className="text-base sm:text-lg mr-2">💭</span>
                      Transcript
                    </h4>
                    <div className="space-y-2 sm:space-y-3 max-h-48 sm:max-h-64 overflow-y-auto scrollbar-thin">
                      {conversationDetails.transcript.map((message: any, index: number) => {
                        const hasText = Boolean(message.message && message.message.trim());
                        const toolCalls = message.tool_calls || [];
                        const toolResults = message.tool_results || [];
                        const displayBlocks: Array<{type:string;content:string}> = [];
                        if (hasText) {
                          displayBlocks.push({type:'text',content:message.message});
                        }
                        if (!hasText && toolCalls.length===0 && toolResults.length===0) {
                          displayBlocks.push({type:'text',content:'[no content]'});
                        }
                        toolCalls.forEach((tc:any)=>{
                          const paramsSnippet = tc.params_as_json ? `params: ${tc.params_as_json}` : '';
                          displayBlocks.push({type:'tool',content:`🔧 Tool call → ${tc.tool_name} ${paramsSnippet}`});
                        });
                        toolResults.forEach((tr:any)=>{
                          const resultSnippet = tr.result_value ? `result: ${tr.result_value}` : '';
                          displayBlocks.push({type:'result',content:`🛠️ Tool result ← ${tr.tool_name} ${resultSnippet}`});
                        });

                        return (
                        <div
                          key={index}
                            className={`p-2 sm:p-3 rounded-xl ${
                            message.role === 'user'
                                ? 'bg-blue-500/20 border border-blue-500/30 ml-2 sm:ml-6'
                                : 'bg-purple-500/20 border border-purple-500/30 mr-2 sm:mr-6'
                          }`}
                        >
                            <div className="flex justify-between items-start mb-1 sm:mb-2">
                              <span className="font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2">
                              <span>{message.role === 'user' ? '👤' : '🤖'}</span>
                              <span className="text-white">
                                {message.role === 'user' ? 'User' : 'Agent'}
                              </span>
                            </span>
                            <span className="text-xs text-gray-400">
                              {message.time_in_call_secs}s
                            </span>
                          </div>
                            {displayBlocks.map((b,i)=>(
                              <p key={i} className="text-[11px] sm:text-xs text-gray-200 leading-relaxed mb-1 whitespace-pre-wrap break-words">
                                {b.content}
                              </p>
                            ))}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {conversationDetails.has_audio && (
                  <div className="space-y-3 sm:space-y-4">
                    <h4 className="font-semibold text-white mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
                      <span className="text-base sm:text-lg mr-2">🎵</span>
                      Audio Recording
                    </h4>
                    
                    {/* Full Conversation Audio */}
                    <AudioPlayer
                      conversationId={selectedConversation}
                      apiKey={apiKey}
                      audioType="full"
                      title="Full Conversation"
                    />
                    
                    <div className="glass-effect rounded-xl p-3 sm:p-4 border border-blue-500/30">
                      <div className="flex items-start space-x-2 sm:space-x-3">
                        <span className="text-blue-400 text-lg sm:text-xl">💡</span>
                        <div>
                          <h5 className="text-white font-medium mb-2">Audio Information</h5>
                          <div className="text-gray-300 text-sm space-y-1">
                            <p>• Full conversation includes both user and agent audio</p>
                            <p>• High-quality audio recording for review and analysis</p>
                            <p>• Audio files are securely stored and can be played multiple times</p>
                            {conversationDetails.has_user_audio && <p>• User audio: Available</p>}
                            {conversationDetails.has_response_audio && <p>• Agent response audio: Available</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16 text-gray-400">
                <div className="text-6xl mb-4">❌</div>
                <h4 className="text-lg font-medium text-gray-300 mb-2">Failed to load details</h4>
                <p>Could not load conversation details. Please try again.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationHistory; 